// Tactical error dialog — uses the app's `fdcTheme` vocabulary so it sits
// alongside accordions/panels/cards instead of feeling like a stock MUI
// material modal. Companion to useStructuredError + ErrorContext.

import React from "react";
import { Dialog, Box, Stack, Typography, Button, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";

import { severityFor } from "../hooks/useStructuredError";
import {
  microLabelSx,
  panelTitleSx,
  tacticalActionSx,
} from "../utils/fdcTheme";

// Friendly headline per error_kind.
const TITLES = {
  OBSERVATION_ON_TARGET: "Round on target — no correction needed",
  OBSERVATION_AT_BATTERY: "Observation at the battery position",
  NO_PRIOR_SHOT: "No previous shot to adjust against",
  UNHANDLED: "Unexpected server error",
  API_ERROR: "API error",
  UNKNOWN: "Something went wrong",
};

// Map MUI severity → fdcTheme tone vocabulary
const SEVERITY_TONE = {
  warning: "warning",
  error: "danger",
};

function toneColor(theme, tone) {
  switch (tone) {
    case "danger":
      return theme.palette.error.main;
    case "warning":
      return theme.palette.warning.main;
    case "success":
      return theme.palette.success.main;
    default:
      return theme.palette.text.primary;
  }
}

export default function ErrorDialog({ error, onClose, onAction }) {
  if (!error) return null;

  const severity = severityFor(error.error_kind);
  const tone = SEVERITY_TONE[severity] || "warning";
  const title = TITLES[error.error_kind] ?? TITLES.UNKNOWN;
  const Icon = tone === "danger" ? ErrorRoundedIcon : ReportProblemRoundedIcon;

  return (
    <Dialog
      open={!!error}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: "none",
          border: "1px solid",
          borderColor: alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 1 : 0.92,
          ),
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 40px ${alpha(theme.palette.common.black, 0.45)}`
              : `0 14px 32px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: "hidden",
        }),
      }}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            bgcolor: alpha(theme.palette.common.black, 0.55),
            backdropFilter: "blur(2px)",
          }),
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={(theme) => {
          const c = toneColor(theme, tone);
          return {
            position: "relative",
            px: 2.25,
            py: 1.75,
            borderBottom: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            bgcolor: alpha(c, theme.palette.mode === "dark" ? 0.1 : 0.05),
            // Coloured left accent bar so the severity is unmistakable.
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: 3,
              bgcolor: c,
            },
          };
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={(theme) => {
              const c = toneColor(theme, tone);
              return {
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(c, theme.palette.mode === "dark" ? 0.18 : 0.1),
                color: c,
                flexShrink: 0,
              };
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={(theme) => ({
                ...microLabelSx,
                color: toneColor(theme, tone),
                mb: 0.4,
              })}
            >
              {(error.error_kind || "ERROR").replace(/_/g, " ")}
            </Typography>
            <Typography
              sx={{
                ...panelTitleSx,
                fontSize: 15.5,
                letterSpacing: -0.1,
                lineHeight: 1.25,
              }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ ml: 0.5, color: "text.secondary" }}
            aria-label="Close"
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 2.25, py: 2 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "text.primary",
            mb: error.hint || error.details ? 1.5 : 0,
          }}
        >
          {error.message}
        </Typography>

        {error.hint && (
          <Box
            sx={(theme) => ({
              p: 1.25,
              borderRadius: 1.65,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.9),
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.035)
                  : alpha(theme.palette.common.black, 0.02),
            })}
          >
            <Typography
              sx={{
                ...microLabelSx,
                color: "text.secondary",
                display: "block",
                mb: 0.7,
              }}
            >
              Diagnostics
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", "Consolas", monospace',
                fontSize: 11.5,
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "pre-wrap",
                lineHeight: 1.55,
              }}
            >
              {error.hint}
            </Typography>
          </Box>
        )}

        {error.details && (
          <Box
            sx={(theme) => ({
              mt: error.hint ? 1.25 : 0,
              p: 1.25,
              borderRadius: 1.65,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.9),
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.035)
                  : alpha(theme.palette.common.black, 0.02),
            })}
          >
            <Typography
              sx={{
                ...microLabelSx,
                color: "text.secondary",
                display: "block",
                mb: 0.7,
              }}
            >
              Details
            </Typography>
            <Typography
              component="pre"
              sx={{
                fontFamily: '"JetBrains Mono", "Consolas", monospace',
                fontSize: 11,
                color: "text.secondary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
                lineHeight: 1.5,
              }}
            >
              {typeof error.details === "string"
                ? error.details
                : JSON.stringify(error.details, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={(theme) => ({
          px: 2.25,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.9),
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.black, 0.18)
              : alpha(theme.palette.common.black, 0.015),
          display: "flex",
          flexWrap: "wrap",
          gap: 0.9,
          justifyContent: "flex-end",
        })}
      >
        {(error.actions || []).map((a) => (
          <Button
            key={a.action}
            onClick={() => onAction?.(a.action, error)}
            sx={(theme) => tacticalActionSx(theme, tone, "outlined")}
          >
            {a.label}
          </Button>
        ))}
        <Button
          onClick={onClose}
          sx={(theme) => tacticalActionSx(theme, tone, "contained")}
        >
          Dismiss
        </Button>
      </Box>
    </Dialog>
  );
}
